import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import FeeScheduleSchema, { FeeScheduleJSON, FeeScheduleParams } from "marketplace/teams/fee-schedules/_schemas/fee-schedule-schema";

export default class FeeSchedule {
  public static create(values: FeeScheduleParams): FeeSchedule {
    const parsed = FeeScheduleSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid FeeSchedule parameters.");
    }
    return new FeeSchedule(parsed);
  }

  public static fromJSON(json: FeeScheduleJSON): FeeSchedule {
    if (isNullOrUndefined(json)) return json;
    const params = FeeScheduleSchema.deserialize(json);
    return new FeeSchedule(params);
  }

  public static fromJSONArray(
    jsonArray: FeeScheduleJSON[]
  ): FeeSchedule[] {
    return jsonArray.map(json => FeeSchedule.fromJSON(json));
  }

  /**
   * A published rate as a proposal takes it: a copy with a new id, still naming who bills it.
   *
   * The new id is because a proposal's fee schedule keys its items by id. Reusing the marketplace
   * category's would tie a negotiated item to the advertised one it came from, and two proposals
   * that took the same rate would collide on identity. The person stays: whether they can bill this
   * proposal is put to the user by `useReconcileFeeScheduleStaffing`, never settled here (#4920).
   */
  public static copyCategoryForProposal(published: FeeScheduleCategory): FeeScheduleCategory {
    return new FeeScheduleCategory({
      id: Guid.generate(),
      name: published.name,
      description: published.description ?? null,
      fee: published.fee ?? null,
      type: published.type,
      teamMember: published.teamMember ?? null,
      teamMemberIsMasked: false,
    });
  }

  public readonly id: Guid;
  public readonly name: string;
  public readonly tags: FeeScheduleTag[] = [];
  public readonly categories: FeeScheduleCategory[] = [];

  /** Reads as a sentence beside the name, so a one-category schedule doesn't say "1 categories". */
  public get categoryCountLabel(): string {
    return this.categories.length === 1 ? "1 category" : `${this.categories.length} categories`;
  }

  public constructor(params: FeeScheduleParams) {
    this.name = params.name;
    this.id = params.id ?? Guid.generate();
    this.tags = params.tags ?? [];
    this.categories = params.categories
      ?.map(cat => FeeScheduleCategory.create(cat))
      .filter((cat): cat is FeeScheduleCategory => cat instanceof FeeScheduleCategory) ?? [];
  }

  /** Every rate this schedule advertises, copied for a proposal. See `copyCategoryForProposal`. */
  public copyCategoriesForProposal(): FeeScheduleCategory[] {
    return this.categories.map((category) => FeeSchedule.copyCategoryForProposal(category));
  }
}
